import type { Parcel } from '../types';

// FedEx predefined package options
const FEDEX_PACKAGES = [
  { value: '', label: 'Custom Dimensions' },
  { value: 'FedExEnvelope', label: 'FedEx Envelope' },
  { value: 'FedExPak', label: 'FedEx Pak' },
  { value: 'FedExBox', label: 'FedEx Box' },
  { value: 'FedExSmallBox', label: 'FedEx Small Box' },
  { value: 'FedExMediumBox', label: 'FedEx Medium Box' },
  { value: 'FedExLargeBox', label: 'FedEx Large Box' },
  { value: 'FedExExtraLargeBox', label: 'FedEx Extra Large Box' },
  { value: 'FedExTube', label: 'FedEx Tube' },
  { value: 'FedEx10kgBox', label: 'FedEx 10kg Box' },
  { value: 'FedEx25kgBox', label: 'FedEx 25kg Box' },
];

interface ParcelFormProps {
  parcel: Parcel;
  onChange: (parcel: Parcel) => void;
  readOnly?: boolean;
  /** Render just the fields, without the `.form-section` wrapper + `<h2>`. */
  hideHeader?: boolean;
}

function ParcelForm({ parcel, onChange, readOnly = false, hideHeader = false }: ParcelFormProps) {
  // An empty field is stored as undefined (never 0), so a stored 0 is always
  // a real typed value and must keep rendering as "0" — `?? ''` below, not
  // `|| ''`, which would erase the keystroke while entering values like 0.5.
  const handleChange = (field: keyof Parcel, value: string) => {
    const numValue = value === '' ? undefined : parseFloat(value);
    onChange({ ...parcel, [field]: numValue });
  };

  const handlePackageChange = (value: string) => {
    if (value) {
      // When selecting a predefined package, clear dimensions
      onChange({
        ...parcel,
        predefined_package: value,
        length: undefined,
        width: undefined,
        height: undefined,
      });
    } else {
      // When switching to custom dimensions, clear predefined package
      onChange({
        ...parcel,
        predefined_package: undefined,
      });
    }
  };

  const usingPredefinedPackage = Boolean(parcel.predefined_package);

  const body = (
    <>
      <div className="form-group">
        <label htmlFor="parcel-package-type">Package Type</label>
        <select
          id="parcel-package-type"
          value={parcel.predefined_package || ''}
          onChange={(e) => handlePackageChange(e.target.value)}
          disabled={readOnly}
        >
          {FEDEX_PACKAGES.map((pkg) => (
            <option key={pkg.value} value={pkg.value}>
              {pkg.label}
            </option>
          ))}
        </select>
        <small style={{ color: 'var(--aged-mahogany-light)', fontSize: '0.85em' }}>
          Select a FedEx package or use custom dimensions
        </small>
      </div>

      <div className="form-group">
        <label htmlFor="parcel-weight">Weight (oz) *</label>
        <input
          id="parcel-weight"
          type="number"
          className="mono"
          min="0.1"
          step="0.1"
          value={parcel.weight ?? ''}
          onChange={(e) => handleChange('weight', e.target.value)}
          placeholder="Weight in ounces"
          required
          readOnly={readOnly}
        />
        <small style={{ color: 'var(--aged-mahogany-light)', fontSize: '0.85em' }}>
          1 lb = 16 oz
        </small>
      </div>

      {!usingPredefinedPackage && (
        <div className="parcel-grid">
          <div className="form-group">
            <label htmlFor="parcel-length">Length (in)</label>
            <input
              id="parcel-length"
              type="number"
          className="mono"
              min="0"
              step="0.1"
              value={parcel.length ?? ''}
              onChange={(e) => handleChange('length', e.target.value)}
              placeholder="Length"
              readOnly={readOnly}
            />
          </div>

          <div className="form-group">
            <label htmlFor="parcel-width">Width (in)</label>
            <input
              id="parcel-width"
              type="number"
          className="mono"
              min="0"
              step="0.1"
              value={parcel.width ?? ''}
              onChange={(e) => handleChange('width', e.target.value)}
              placeholder="Width"
              readOnly={readOnly}
            />
          </div>

          <div className="form-group">
            <label htmlFor="parcel-height">Height (in)</label>
            <input
              id="parcel-height"
              type="number"
          className="mono"
              min="0"
              step="0.1"
              value={parcel.height ?? ''}
              onChange={(e) => handleChange('height', e.target.value)}
              placeholder="Height"
              readOnly={readOnly}
            />
          </div>
        </div>
      )}

      {usingPredefinedPackage && (
        <div style={{
          padding: '12px',
          backgroundColor: 'var(--aged-mahogany-bg, #f5f5f5)',
          borderRadius: '4px',
          fontSize: '0.9em',
          color: 'var(--aged-mahogany-light, #666)'
        }}>
          Using predefined package dimensions from FedEx
        </div>
      )}
    </>
  );

  if (hideHeader) return body;

  return (
    <section className="form-section" aria-labelledby="parcel-heading">
      <h2 id="parcel-heading">Package Details</h2>
      {body}
    </section>
  );
}

export default ParcelForm;
